import { LAYOUT_KEYS, } from '@/types/hangingProtocol';
// Derived, not re-typed: a new layout added to the union is accepted here for
// free. Stays ReadonlySet<string> because .has() takes unvalidated wire data.
const VALID_LAYOUTS = new Set(LAYOUT_KEYS);
/** Primary viewer window name (the existing cockpit leg). */
export const PRIMARY_VIEWER_WINDOW_NAME = 'newlumen-viewer';
/** Window name for extra viewer span N (1-based extra index → viewer-2, viewer-3…). */
export function viewerWindowNameForSpan(spanIndex) {
    return spanIndex === 0 ? PRIMARY_VIEWER_WINDOW_NAME : `newlumen-viewer-${spanIndex + 1}`;
}
/**
 * Which span this window embodies: 'newlumen-viewer' → 0,
 * 'newlumen-viewer-2' → 1, … Anything else (a plain tab, the report window)
 * is span 0 — the base hang.
 */
export function viewerSpanIndexFromWindowName(windowName) {
    if (!windowName)
        return 0;
    const match = /^newlumen-viewer-(\d+)$/.exec(windowName);
    if (!match)
        return 0;
    const n = Number(match[1]);
    return Number.isInteger(n) && n >= 2 ? n - 1 : 0;
}
/**
 * Sanitize a stored `monitorLayouts` value. The viewer-settings blob round-trips
 * through the backend verbatim and older/corrupted entries must not take the
 * apply effect down (there was a live incident of a corrupted blob re-infecting
 * every browser) — mergeWithDefaults runs everything through here.
 * Returns undefined unless there are ≥2 well-formed spans (a single span is
 * just the base layout and carries no information).
 */
export function sanitizeMonitorLayouts(raw) {
    if (!Array.isArray(raw))
        return undefined;
    const spans = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== 'object')
            return undefined;
        const e = entry;
        if (typeof e.layout !== 'string' || !VALID_LAYOUTS.has(e.layout))
            return undefined;
        if (!Array.isArray(e.viewports) || e.viewports.length === 0)
            return undefined;
        const viewports = [];
        for (const slot of e.viewports) {
            if (!slot || typeof slot !== 'object')
                return undefined;
            const sl = slot;
            if (typeof sl.id !== 'string' || typeof sl.label !== 'string')
                return undefined;
            viewports.push({
                id: sl.id,
                label: sl.label,
                sublabel: typeof sl.sublabel === 'string' ? sl.sublabel : '',
                role: sl.role === 'prior' ? 'prior' : 'current',
                wlPreset: typeof sl.wlPreset === 'string'
                    ? sl.wlPreset
                    : 'soft-tissue',
            });
        }
        spans.push({ layout: e.layout, viewports });
    }
    return spans.length >= 2 ? spans : undefined;
}
/**
 * The spans a protocol will actually run with on this workstation, or null for
 * the plain single-window hang. `liveViewerScreens` is how many screens carry
 * the viewer role right now.
 */
export function activeMonitorSpans(protocol, multiMonitorEnabled, liveViewerScreens) {
    const spans = sanitizeMonitorLayouts(protocol.monitorLayouts);
    if (!spans)
        return null;
    if (!multiMonitorEnabled)
        return null;
    if (liveViewerScreens < spans.length)
        return null;
    return spans;
}
/**
 * The layout + slots THIS window should hang. Falls back to the protocol's
 * base layout/viewports whenever spans are inactive or this window's span
 * doesn't exist (e.g. a stale `newlumen-viewer-3` window after the protocol
 * was edited down to two spans).
 */
export function spanForWindow(protocol, spanIndex, multiMonitorEnabled, liveViewerScreens) {
    const spans = activeMonitorSpans(protocol, multiMonitorEnabled, liveViewerScreens);
    if (spans && spanIndex >= 0 && spanIndex < spans.length)
        return spans[spanIndex];
    return { layout: protocol.layout, viewports: protocol.viewports ?? [] };
}
